import { AlertCircle } from "lucide-react";
import type { ReactNode } from "react";

import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import { cn } from "utils";

/**
 * A labelled text field with a character counter against the rules' limit.
 *
 * The counter is plain text tied to the field with `aria-describedby`, not a
 * live region: announcing every keystroke's count would drown the field. It
 * turns red past the limit, and the error beneath says what to do, because the
 * rules answer an over-long value with a permission-denied that names nothing.
 * Nothing stops typing at the limit, so a pasted paragraph is shown as too
 * long rather than silently cut.
 */
export function LimitedField({
  id,
  label,
  value,
  max,
  count = value.length,
  onChange,
  multiline = false,
  rows = 3,
  hint,
  error,
  disabled,
  placeholder,
  labelHidden = false,
  invalid = false,
  describedBy: sharedError,
}: {
  /** Also the data-testid of the control; the counter and error derive from it. */
  id: string;
  label: string;
  value: string;
  max: number;
  /** What to count when it is not the raw text, as for the headline. */
  count?: number;
  onChange: (next: string) => void;
  multiline?: boolean;
  rows?: number;
  hint?: ReactNode;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  /** For a field whose visible label is carried by a fieldset legend. */
  labelHidden?: boolean;
  /** Marks the field invalid when the message for it is shown elsewhere. */
  invalid?: boolean;
  /** The id of that message, so the field is described by it. */
  describedBy?: string;
}) {
  const hintId = `${id}-hint`;
  const countId = `${id}-count`;
  const errorId = `${id}-error`;
  const describedBy =
    [hint ? hintId : null, countId, error ? errorId : null, sharedError]
      .filter(Boolean)
      .join(" ") || undefined;
  const common = {
    id,
    "data-testid": id,
    value,
    disabled,
    placeholder,
    "aria-invalid": error || invalid ? true : undefined,
    "aria-describedby": describedBy,
  };
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className={cn(labelHidden && "sr-only")}>
        {label}
      </Label>
      {multiline ? (
        <Textarea
          {...common}
          rows={rows}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <Input {...common} onChange={(e) => onChange(e.target.value)} />
      )}
      <div className="flex items-start justify-between gap-3 text-xs">
        <div className="text-muted-foreground">
          {hint && (
            <p id={hintId} data-testid={hintId}>
              {hint}
            </p>
          )}
        </div>
        <span
          id={countId}
          data-testid={countId}
          className={cn(
            "shrink-0 tabular-nums",
            count > max
              ? "font-semibold text-destructive-text"
              : "text-muted-foreground",
          )}
        >
          {count}/{max}
        </span>
      </div>
      {error && (
        <p
          id={errorId}
          role="alert"
          className="flex items-center gap-2 text-sm text-destructive-text"
          data-testid={errorId}
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
}
